/**
 * Interactive breakpoint/step debugger for a workflow `js_code_transform` node
 * (ADR-0067 S4) — the flows-plane twin of the App-builder `QueryDebugPanel`.
 *
 * The code node has NO `ctx.db`, so the koush engine steps it as PURE JS (no source
 * map, no host bindings) on ONE sampled input row. The operator sets breakpoints in
 * the gutter, presses ▶ Avvia, and steps through — production stays the pooled Fast
 * mode. Side-effect-free by construction. Graceful-off when the debug engine isn't
 * built (`available:false` → a hint, the control still shows the code read-only).
 */

import * as React from "react";
import { useEffect, useRef, useState } from "react";
import Editor, { type Monaco } from "@monaco-editor/react";
import {
  Bug,
  ChevronsRight,
  CornerDownRight,
  CornerLeftUp,
  Play,
  RotateCcw,
  Square,
  X,
} from "lucide-react";

import {
  CodeNodeDebugSession,
  type DebugEvent,
  type DebugVar,
  type StepKind,
  codeNodeDebugSocketUrl,
  fetchCodeNodeBreakpointLines,
} from "@/lib/codeNodeDebug";
import { useAuthStore } from "@/lib/auth";
import { useTheme } from "@/theme/ThemeProvider";

// Derive the Monaco types from the `Editor` component (monaco-editor isn't a direct
// dep — the same trick CodeEditor.tsx / QueryDebugPanel.tsx use).
type EditorInstance = Parameters<NonNullable<React.ComponentProps<typeof Editor>["onMount"]>>[0];
type MouseEvt = Parameters<Parameters<EditorInstance["onMouseDown"]>[0]>[0];
type Decoration = Parameters<EditorInstance["deltaDecorations"]>[1][number];

type Status = "idle" | "connecting" | "running" | "paused" | "terminated" | "error";

interface Props {
  workflowId: string;
  nodeId: string;
  /** The editor-buffer code (the operator debugs what they see). */
  code: string;
  /** One sample item's data — becomes `payload` in `run(payload)`. */
  sampleInput?: unknown;
  onClose: () => void;
}

export function CodeNodeDebugPanel({ workflowId, nodeId, code, sampleInput, onClose }: Props) {
  const token = useAuthStore((s) => s.token);
  const { resolved } = useTheme();

  const [status, setStatus] = useState<Status>("idle");
  const [line, setLine] = useState<number | null>(null);
  const [vars, setVars] = useState<DebugVar[]>([]);
  const [result, setResult] = useState<{ ok: boolean; value: unknown; error: string | null } | null>(
    null,
  );
  const [message, setMessage] = useState("");
  const [started, setStarted] = useState(false);
  const [runNonce, setRunNonce] = useState(0);
  const [inputText, setInputText] = useState(() =>
    JSON.stringify(sampleInput ?? {}, null, 2),
  );
  const inputTextRef = useRef(inputText);
  inputTextRef.current = inputText;

  const sessionRef = useRef<CodeNodeDebugSession | null>(null);
  const editorRef = useRef<EditorInstance | null>(null);
  const monacoRef = useRef<Monaco | null>(null);
  const decorationsRef = useRef<string[]>([]);
  const bpRef = useRef<Set<number>>(new Set());
  // The editor lines a breakpoint can bind to (QuickJS pc2line). null = unconfined
  // (not fetched / old engine → allow any line). A ref so the Monaco mouse handler
  // reads it without re-binding.
  const execLinesRef = useRef<Set<number> | null>(null);
  const [execLines, setExecLines] = useState<Set<number> | null>(null);

  // Fetch the executable lines once per (node, code) so a breakpoint can't sit on a
  // blank line. Graceful: any failure leaves it unconfined.
  useEffect(() => {
    let alive = true;
    setExecLines(null);
    execLinesRef.current = null;
    fetchCodeNodeBreakpointLines(workflowId, nodeId, code)
      .then((res) => {
        if (!alive) return;
        if (res.available) {
          const set = new Set(res.lines);
          execLinesRef.current = set;
          setExecLines(set);
        }
      })
      .catch(() => {
        /* old engine / no access → leave unconfined */
      });
    return () => {
      alive = false;
    };
  }, [workflowId, nodeId, code]);

  // Open the session when the developer presses ▶ Avvia (via started + runNonce), NOT
  // on mount — so breakpoints can be placed first.
  useEffect(() => {
    if (!token || !started) return;
    setStatus("connecting");
    setLine(null);
    setVars([]);
    setResult(null);
    setMessage("");
    let parsedInput: unknown = {};
    try {
      parsedInput = JSON.parse(inputTextRef.current || "{}");
    } catch {
      parsedInput = {}; // invalid JSON → debug with empty input
    }
    const url = codeNodeDebugSocketUrl(workflowId, nodeId, token);
    const session = new CodeNodeDebugSession(url, code, parsedInput, {
      onEvent: (ev: DebugEvent) => {
        switch (ev.type) {
          case "ready":
            setStatus("running");
            session.setBreakpoints([...bpRef.current].sort((a, b) => a - b));
            break;
          case "stopped":
            // koush ALWAYS halts at `entry` first; skip it (Avvia = RUN) so it goes to
            // the first real breakpoint — or to the end.
            if (ev.reason === "entry") {
              setStatus("running");
              session.resume("continue");
              break;
            }
            setStatus("paused");
            setLine(ev.line);
            session.requestVars();
            break;
          case "vars":
            setVars(ev.vars);
            break;
          case "terminated":
            setStatus("terminated");
            setResult({ ok: ev.ok, value: ev.value, error: ev.error });
            setLine(null);
            break;
          case "error":
            setStatus("error");
            setMessage(ev.error);
            break;
        }
      },
      onClose: () => setStatus((s) => (s === "connecting" ? "error" : s)),
    });
    sessionRef.current = session;
    session.connect();
    return () => session.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId, nodeId, token, started, runNonce]);

  const finished = status === "terminated" || status === "error";
  const notStarted = status === "idle";
  const paused = status === "paused";

  function start() {
    sessionRef.current?.close();
    setStarted(true);
    setRunNonce((n) => n + 1);
  }
  function stop() {
    sessionRef.current?.close();
    setStarted(false);
    setStatus("idle");
    setLine(null);
    setVars([]);
  }
  function step(kind: StepKind) {
    if (!paused) return;
    setStatus("running");
    setVars([]);
    setLine(null);
    sessionRef.current?.resume(kind);
  }

  // ---- Monaco: breakpoint gutter + current-line highlight
  function handleMount(ed: EditorInstance, monaco: Monaco) {
    editorRef.current = ed;
    monacoRef.current = monaco;
    ed.updateOptions({ glyphMargin: true, readOnly: true });
    ed.onMouseDown((e: MouseEvt) => {
      if (e.target.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN) {
        const ln = e.target.position?.lineNumber;
        if (ln) toggleBreakpoint(ln);
      }
    });
    renderDecorations();
  }

  function toggleBreakpoint(ln: number) {
    const set = bpRef.current;
    // Confine a NEW breakpoint to an executable line (removing one is always allowed).
    const exec = execLinesRef.current;
    if (!set.has(ln) && exec && !exec.has(ln)) return;
    if (set.has(ln)) set.delete(ln);
    else set.add(ln);
    sessionRef.current?.setBreakpoints([...set].sort((a, b) => a - b));
    renderDecorations();
  }

  function renderDecorations() {
    const ed = editorRef.current;
    const monaco = monacoRef.current;
    if (!ed || !monaco) return;
    const decs: Decoration[] = [];
    for (const ln of bpRef.current) {
      decs.push({
        range: new monaco.Range(ln, 1, ln, 1),
        options: { isWholeLine: false, glyphMarginClassName: "tray-dbg-breakpoint" },
      });
    }
    if (line != null && line > 0) {
      decs.push({
        range: new monaco.Range(line, 1, line, 1),
        options: { isWholeLine: true, className: "tray-dbg-currentline" },
      });
    }
    decorationsRef.current = ed.deltaDecorations(decorationsRef.current, decs);
  }

  useEffect(() => {
    renderDecorations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [line]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <style>{`
        .tray-dbg-breakpoint { background: hsl(var(--danger)); border-radius: 50%;
          width: 10px !important; height: 10px !important; margin-left: 5px; margin-top: 5px; }
        .tray-dbg-currentline { background: hsl(var(--warning) / 0.18); }
      `}</style>

      {/* toolbar */}
      <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5 text-sm">
        <Bug className="h-4 w-4 text-danger" />
        <span className="font-medium">Debug</span>
        <span
          className="text-[11px] text-muted-foreground"
          title="Il debugger esegue il codice come JS puro su una riga campione (nessun ctx.db). È un dry-run: non produce output reali."
        >
          JS puro · dry-run
        </span>
        <StatusChip status={status} line={line} />
        <div className="ml-2 flex items-center gap-1">
          <IconBtn
            title={notStarted ? "Avvia (esegui fino al primo breakpoint)" : "Continua (F8)"}
            onClick={notStarted ? start : () => step("continue")}
            disabled={notStarted ? false : !paused}
          >
            <Play className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Step over (F10)" onClick={() => step("stepOver")} disabled={!paused}>
            <ChevronsRight className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Step into (F11)" onClick={() => step("stepIn")} disabled={!paused}>
            <CornerDownRight className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Step out (Shift+F11)" onClick={() => step("stepOut")} disabled={!paused}>
            <CornerLeftUp className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Ferma (torna a modifica breakpoint)" onClick={stop} disabled={notStarted}>
            <Square className="h-4 w-4" />
          </IconBtn>
          <IconBtn title="Riavvia il debug" onClick={start} disabled={!finished}>
            <RotateCcw className="h-4 w-4" />
          </IconBtn>
        </div>
        <button
          className="ml-auto rounded p-1 text-muted-foreground hover:bg-elevated"
          title="Chiudi debug"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* code + breakpoint gutter */}
        <div className="min-h-0 min-w-0 flex-1">
          <Editor
            height="100%"
            language="javascript"
            value={code}
            onMount={handleMount}
            theme={resolved === "dark" ? "vs-dark" : "vs"}
            options={{
              readOnly: true,
              glyphMargin: true,
              minimap: { enabled: false },
              fontSize: 13,
              lineNumbers: "on",
              scrollBeyondLastLine: false,
            }}
          />
        </div>

        {/* right rail: input + variables + result */}
        <div className="flex w-72 shrink-0 flex-col border-l border-border">
          <div className="border-b border-border p-2">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold text-muted-foreground">Input · payload</span>
              <span className="text-[10px] text-muted-foreground">JSON · ↺ per applicare</span>
            </div>
            <textarea
              className="h-20 w-full resize-y rounded border border-border bg-elevated px-1.5 py-1 font-mono text-[11px] outline-none focus:border-accent"
              spellCheck={false}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder='{"id": "…"}'
            />
            <button
              className="mt-1 flex w-full items-center justify-center gap-1 rounded border border-border px-2 py-1 text-[11px] text-foreground hover:bg-elevated"
              onClick={start}
              title="Avvia il debug con questo input"
            >
              {notStarted ? <Play className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}{" "}
              {notStarted ? "Avvia con questo input" : "Riavvia con questo input"}
            </button>
          </div>

          <div className="border-b border-border px-2 py-1 text-xs font-semibold text-muted-foreground">
            Variabili {paused ? `(linea ${line ?? "?"})` : ""}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-1.5">
            {vars.length === 0 ? (
              <div className="px-1 py-1 text-xs text-muted-foreground">
                {paused ? "nessuna variabile in scope" : "in pausa a un breakpoint per ispezionare"}
              </div>
            ) : (
              <div className="flex flex-col gap-1.5">
                {vars.map((v) => (
                  <div key={v.name} className="rounded border border-border/60 bg-elevated/40">
                    <div className="border-b border-border/50 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-foreground">
                      {v.name}
                    </div>
                    <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words px-1.5 py-1 font-mono text-[11px] text-muted-foreground">
                      {prettyVar(v.value)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
          {result && (
            <div className="border-t border-border p-2 text-xs">
              <div className="mb-1 font-semibold text-muted-foreground">Risultato</div>
              {result.ok ? (
                <pre className="max-h-40 overflow-auto rounded bg-elevated p-1.5 font-mono">
                  {JSON.stringify(result.value, null, 2)}
                </pre>
              ) : (
                <div className="rounded bg-danger/10 p-1.5 font-mono text-danger">{result.error}</div>
              )}
            </div>
          )}
          {status === "error" && message && (
            <div className="border-t border-border p-2 font-mono text-xs text-danger">{message}</div>
          )}
          {notStarted && (
            <div className="border-t border-border px-2 py-1.5 text-[11px] text-muted-foreground">
              Clicca a sinistra dei numeri di riga per i breakpoint, poi ▶ Avvia.
              {execLines && " Solo le righe eseguibili accettano un breakpoint."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** The backend sends each variable's value as a compact JSON string (or a primitive
 *  repr). Indent it for the inspector. */
function prettyVar(value: string): string {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length === 0) {
      return Array.isArray(parsed) ? "[]" : "{}  (oggetto senza campi serializzabili)";
    }
    return JSON.stringify(parsed, null, 2);
  } catch {
    return value; // a primitive / non-JSON repr (e.g. "undefined", "function …")
  }
}

function StatusChip({ status, line }: { status: Status; line: number | null }) {
  const map: Record<Status, { label: string; cls: string }> = {
    idle: { label: "pronto · imposta i breakpoint", cls: "bg-muted text-muted-foreground" },
    connecting: { label: "connessione…", cls: "bg-muted text-muted-foreground" },
    running: { label: "in esecuzione", cls: "bg-info/15 text-info" },
    paused: { label: `in pausa · linea ${line ?? "?"}`, cls: "bg-warning/20 text-warning" },
    terminated: { label: "terminato", cls: "bg-success/15 text-success" },
    error: { label: "errore", cls: "bg-danger/15 text-danger" },
  };
  const m = map[status];
  return <span className={`rounded px-1.5 py-0.5 text-[11px] ${m.cls}`}>{m.label}</span>;
}

function IconBtn({
  children,
  onClick,
  disabled,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      className="rounded p-1 text-foreground hover:bg-elevated disabled:opacity-40"
      onClick={onClick}
      disabled={disabled}
      title={title}
    >
      {children}
    </button>
  );
}
